// Bereavement case API contracts. Shared between web and api.

import { z } from 'zod';
// Type-only — keeps the cases↔preferences import cycle erased at runtime
// (preferences.ts depends on this module's TouchpointType at runtime; this
// module depends on preferences.ts only for a type).
import type { EmployeeSupportPreferences } from './preferences.ts';
import type { RoleVisiblePreferences } from './preference-visibility.ts';
import type { SignificantDatesForRole } from './significant-dates.ts';

// Free-form per DESIGN §3.2.2 (`deceased_relationship: String/Enum`); we
// constrain to a sensible MVP set in the API contract so analytics has
// stable categories. Broaden when product needs it.
export const DECEASED_RELATIONSHIPS = [
  'Parent',
  'Partner',
  'Child',
  'Sibling',
  'Grandparent',
  'Other',
] as const;
export type DeceasedRelationship = (typeof DECEASED_RELATIONSHIPS)[number];

// Support Framework (touch points) spec §19 — loss type drives sub-framework
// routing (Slice 10). DISTINCT from `deceasedRelationship` (who died): loss
// type captures the *kind* of loss for differentiated clinical support.
// `Prefer_Not_To_Say` is an explicit employee/HR choice; an omitted field
// (null on the case) means "not recorded" — the spec is careful not to force
// unnecessary detail (§19), so the create form treats it as optional.
export const LOSS_TYPES = [
  'Parent',
  'Partner_Spouse',
  'Child',
  'Sibling',
  'Friend',
  'Pregnancy_Loss',
  'Stillbirth_Neonatal',
  'Suicide_Bereavement',
  'Sudden_Traumatic',
  'Other',
  'Prefer_Not_To_Say',
] as const;
export type LossType = (typeof LOSS_TYPES)[number];

// Support Framework spec §19 — loss types that require a differentiated
// sub-framework ("certain loss types require differentiated support … including
// suicide bereavement, loss of a child, pregnancy and neonatal loss, and sudden
// or traumatic loss"). For these, the base framework is modified by loss-type-
// specific guidance and the case is flagged for HR review on creation (§19 #5).
export const LOSS_SUBFRAMEWORK_TYPES = [
  'Child',
  'Pregnancy_Loss',
  'Stillbirth_Neonatal',
  'Suicide_Bereavement',
  'Sudden_Traumatic',
] as const satisfies readonly LossType[];

export function isLossSubframeworkType(lossType: LossType | null | undefined): boolean {
  return lossType != null && (LOSS_SUBFRAMEWORK_TYPES as readonly string[]).includes(lossType);
}

// §19 #5 — a sub-framework loss type flags the case for HR review on creation.
export function lossTypeRequiresHrReview(lossType: LossType | null | undefined): boolean {
  return isLossSubframeworkType(lossType);
}

// Human-readable labels for the loss-type select + HR/Manager case views.
// Keep in sync with `LOSS_TYPES`.
export const LOSS_TYPE_LABELS: Record<LossType, string> = {
  Parent: 'Parent',
  Partner_Spouse: 'Partner / spouse',
  Child: 'Child',
  Sibling: 'Sibling',
  Friend: 'Friend',
  Pregnancy_Loss: 'Pregnancy loss',
  Stillbirth_Neonatal: 'Stillbirth / neonatal loss',
  Suicide_Bereavement: 'Suicide bereavement',
  Sudden_Traumatic: 'Sudden or traumatic loss',
  Other: 'Other / not specified',
  Prefer_Not_To_Say: 'Prefer not to record',
};

export const CASE_STATUSES = ['Open', 'Quiet', 'Closed'] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

// HR dashboard "On track / Due soon / Overdue / Paused" tiles — a deep-link
// filter for `/hr/cases`, NOT a stored case status. A case's bucket is
// DERIVED (mirrors the assignment in `routes/hr.ts`'s `GET /dashboard`
// handler, and the matching SQL condition in
// `CasesRepository.bucketCondition`, which both must agree with this list
// so a tile's count and its filtered list can never disagree):
//   paused  = status 'Quiet'
//   overdue = status 'Open' AND an overdue touch point
//   dueSoon = status 'Open', no overdue touch point, RTW date within 7 days
//   onTrack = status 'Open', everything else
// A 'Closed' case never matches any bucket.
export const CASE_BUCKETS = ['onTrack', 'dueSoon', 'overdue', 'paused'] as const;
export type CaseBucket = (typeof CASE_BUCKETS)[number];

// Single-sourced display labels — used by both the dashboard tiles
// (`HrDashboard.tsx`'s `BucketTile`) and the `/hr/cases` filter-pill they
// deep-link to, so the two can't drift apart.
export const CASE_BUCKET_LABELS: Record<CaseBucket, string> = {
  onTrack: 'On track',
  dueSoon: 'Due soon',
  overdue: 'Overdue',
  paused: 'Paused',
};

// GET /api/hr/cases query params — search + filter + pagination.
export const ListCasesRequestSchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(CASE_STATUSES).optional(),
  managerId: z.string().uuid().optional(),
  bucket: z.enum(CASE_BUCKETS).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type ListCasesRequest = z.infer<typeof ListCasesRequestSchema>;

// GET /api/hr/cases/export query params — the same filters as
// `ListCasesRequestSchema`, deliberately WITHOUT `limit`/`offset`: this
// endpoint always returns every matching case (up to an internal safety
// cap), not a page.
export const ExportCasesRequestSchema = ListCasesRequestSchema.omit({ limit: true, offset: true });
export type ExportCasesRequest = z.infer<typeof ExportCasesRequestSchema>;

// GET /api/cases (Manager's own list) query params — derived from
// ListCasesRequestSchema minus managerId (forced server-side from the
// session, never client-supplied) and minus limit's default: omitting
// limit must mean "everything" (Maeve-First's zero-param calls rely on
// this), not "50".
export const ListMyCasesRequestSchema = ListCasesRequestSchema.omit({
  managerId: true,
  limit: true,
}).extend({
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type ListMyCasesRequest = z.infer<typeof ListMyCasesRequestSchema>;

// A touch point is overdue when its status is still `Pending` and the scheduled
// date is more than 48h in the past — DESIGN §9.2 #5. Computed on read; no
// stored field. The threshold lives here in shared so the API computes it the
// same way the UI describes it.
export const OVERDUE_TOUCHPOINT_THRESHOLD_HOURS = 48;
export const OVERDUE_TOUCHPOINT_THRESHOLD_MS = OVERDUE_TOUCHPOINT_THRESHOLD_HOURS * 60 * 60 * 1000;

// A first response is overdue when the case is `Open`, no first response has
// been marked sent, and the case was created more than 24h ago — DESIGN §6.4 /
// §9.2 #7. Computed on read; no stored field. Manager marks via
// `POST /api/cases/:id/mark-first-response-sent` (slice 9); M3 Sub-mode A
// will auto-set the same column without schema change.
export const OVERDUE_FIRST_RESPONSE_THRESHOLD_HOURS = 24;
export const OVERDUE_FIRST_RESPONSE_THRESHOLD_MS =
  OVERDUE_FIRST_RESPONSE_THRESHOLD_HOURS * 60 * 60 * 1000;

export interface CaseSummary {
  id: string;
  employee: { id: string; name: string };
  manager: { id: string; name: string };
  deceasedRelationship: DeceasedRelationship;
  // Support Framework spec §19 — loss type for sub-framework routing.
  // `null` when not recorded. HR/Manager-visible; intentionally NOT on
  // `EmployeeCaseSummary`.
  lossType: LossType | null;
  // Support Framework spec §18 — the framework version stamped at creation.
  frameworkVersion: string;
  dateOfLoss: string; // ISO YYYY-MM-DD
  status: CaseStatus;
  returnToWorkDate: string | null;
  firstResponseSentAt: string | null; // ISO 8601
  createdAt: string; // ISO 8601
  // True when at least one touch point on the case is `Pending` AND its
  // scheduled date is more than 48h in the past — DESIGN §9.2 #5.
  hasOverdueTouchpoint: boolean;
  // True when the employee has recorded a significant date
  // (packages/shared/src/significant-dates.ts) whose next occurrence falls
  // within `SIGNIFICANT_DATE_REMINDER_WINDOW_DAYS`. This is Maeve-internal
  // plumbing for `renderCaseBlock`'s Manager-mode directive — it carries no
  // label/date (that's the Manager-visibility ceiling), so the frontend
  // should render the dedicated `significantDates` projection instead of this
  // raw boolean, to avoid the two ever disagreeing in the UI.
  hasUpcomingSignificantDate: boolean;
  // True when the case is `Open`, no first response has been marked sent,
  // and the case was created more than 24h ago — DESIGN §6.4 / §9.2 #7.
  hasOverdueFirstResponse: boolean;
  // PO Spec §5 (2026-06-21) — Nudge-Free principle + privacy boundary.
  // HR-visible: 'Yes' if the Employee has ever loaded the portal for
  // this case, 'No' otherwise. This is the ONLY field the HR surface
  // is permitted to see — the open timestamps, count, and route-stamps
  // never cross the API boundary (privacy defence in depth on both
  // the repo and the API layer). Defaults to 'No' for cases that
  // haven't been opened yet.
  portalOpened: 'Yes' | 'No';
  // PO Spec Phase 4 §3 (2026-06-21) — "Support Space Activated".
  // HR-visible: 'Yes' if the Employee has had a real two-way
  // engagement with Maeve (first non-empty assistant turn), 'No'
  // otherwise. Distinct from `portalOpened`: a user can open the
  // portal without ever talking to Maeve, and a user can have a
  // portal session with no support engagement (read entitlements,
  // no conversation). Boolean only — the activation timestamp
  // never crosses the API boundary.
  supportSpaceActivated: 'Yes' | 'No';
  // PO Spec Phase 4 §3 (2026-06-21) — "Resources Accessed".
  // HR-visible per-case privacy-bounded fields:
  //   - `resourcesAccessed`: 'Yes' if the Employee has engaged with
  //     any bereavement resource (crisis-link click OR policy-chunk
  //     read), 'No' otherwise. Mirrors the `portalOpened` boolean
  //     posture.
  //   - `resourcesAccessedCount`: total count of resource access
  //     events for this case (per-case view, no per-kind attribution).
  //     Always 0 for cases that have never accessed a resource.
  // The per-event timestamps, the per-kind breakdown, and the
  // resource refs never cross the API boundary — see the
  // `resource_access_events` privacy comment in `schema.ts`.
  resourcesAccessed: 'Yes' | 'No';
  resourcesAccessedCount: number;
}

// GET /api/hr/cases response shape. `cases` is just the current page —
// `total`/`limit`/`offset` describe the full filtered result set.
// `limit`/`offset` are optional because the self-scoped Manager list
// (GET /api/cases) can be called unbounded (no limit requested) — that
// response omits them rather than faking a value. HR's `/hr/cases`
// always supplies both (its schema defaults `limit` to 50).
export interface CasesListResponse {
  cases: CaseSummary[];
  total: number;
  limit?: number;
  offset?: number;
}

// Date-of-loss is ISO YYYY-MM-DD because times-of-day aren't meaningful here
// and a date-only input avoids timezone drift between client and server.
//
// PO Spec §2 (2026-06-21) — the case-create form has EXACTLY three fields
// from the user's perspective: Employee Name, Relationship, Timing (where
// "Timing" is the date of loss — a date is the only timing signal that
// matters for the 12-month ASF projection). `managerId` is NEVER on the
// wire — the manager is always derived server-side from
// `user_manager_mappings`. If the employee is unmapped, the API returns
// 422 `employee_unmapped` and the UI deep-links to /hr/members so the
// HR Admin can set the mapping first. This is the "single-screen form"
// the spec demands — no manager picker, no override path.
//
// `.strict()` rejects any unknown field — the prior `managerId` override
// path is removed at the wire level. A request that supplies `managerId`
// (or any other unknown key) returns 400 `invalid_body` immediately
// rather than silently stripping the field. This is the privacy + UX
// backstop: an HR Admin trying the prior override path gets a clear
// "this field is gone" signal rather than a confusing 422.
export const CreateCaseRequestSchema = z
  .object({
    employeeId: z.string().uuid(),
    deceasedRelationship: z.enum(DECEASED_RELATIONSHIPS),
    // Support Framework spec §19 — optional loss type. Omitted = not recorded
    // (stored null). The spec is explicit about not forcing detail, so this is
    // never required; `Prefer_Not_To_Say` is the explicit "asked, declined".
    lossType: z.enum(LOSS_TYPES).optional(),
    dateOfLoss: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
      .refine((s) => !Number.isNaN(Date.parse(s)), 'Invalid date'),
  })
  .strict();
export type CreateCaseRequest = z.infer<typeof CreateCaseRequestSchema>;

// HR reassigns which Manager a case is routed to (case-scoped only — does
// NOT touch the employee's default `user_manager_mappings` entry; that's a
// separate action on /hr/members). POST /api/hr/cases/:id/reassign-manager.
export const SetCaseManagerRequestSchema = z.object({ managerId: z.string().uuid() });
export type SetCaseManagerRequest = z.infer<typeof SetCaseManagerRequestSchema>;

// PO Spec §4 (2026-06-21) — HR Oversight Dashboard flag kinds.
// Mirrors the `flagType` enum in `@besideu/db` (Phase 0.4) so the
// SPA / API can refer to kinds by name without re-declaring the
// union. The DB column is plain `text` (no SQL CHECK) so adding
// kinds is type-only here.
export const HR_FLAG_TYPES = [
  'Case_Created',
  'First_Response_Sent',
  'Manager_Inactivity',
  'Overdue_Touchpoint',
  'Consecutive_Misses',
  'Amber_Maeve',
  'Red_Maeve',
  'Missing_Manager_Response',
  'RTW_Plan_Unacknowledged',
  'Distress_Amber',
  'Distress_Red',
  // Support Framework spec §17 — manager changed the RTW date and chose
  // "Ask HR to review" rather than auto-recalculating the support dates.
  'RTW_Date_Review',
  // Support Framework spec §19 — case opened with a sub-framework loss type
  // (suicide / child / pregnancy-neonatal / sudden-traumatic) that requires HR
  // review of the differentiated support.
  'Loss_Type_Review',
  // HR deactivated a Manager who still had this case open — the manager can
  // no longer act on it and needs reassigning.
  'Manager_Deactivated',
  // HR changed a Manager's role away from `Manager` while this case was
  // still open — same "needs reassigning" signal as `Manager_Deactivated`,
  // distinct kind since the person is still an active org member.
  'Manager_Role_Changed',
] as const;
export type FlagType = (typeof HR_FLAG_TYPES)[number];

// Six-touchpoint touch point cadence per the Unified MVP Master Spec (2026-05-12)
// — DESIGN §3.2.2 / §6.3. `Day_3`, `Week_1`, `Month_1`, `Month_3` are anchored
// on `dateOfLoss` (engineering decision 2026-05-12 — see PROJECT §6.1).
// `Pre_Return` (T-48h) and `Week_1_Back` (T+7d) are written separately when
// `return_to_work_date` is confirmed via the RTW flow (DESIGN §6.5, M4).
//
// PO Spec §3 (2026-06-21) — the spec's Automated Support Framework™ Engine
// expands the series to 7 + Anniversary touchpoints over a 12-month horizon:
// `Day_3`, `Week_1`, `Month_1`, `Month_3`, `Month_6`, `Month_9`, `Month_12`,
// `Anniversary` (all anchored to `dateOfLoss`); `Pre_Return` + `Week_1_Back`
// stay RTW-anchored. The Anniversary touchpoint lands 1 day after the
// 12-month mark from `dateOfLoss` so the cadence reads as "you've been
// here" rather than "it's been a year" (the email copy carries the actual
// remembering language).
export const TOUCHPOINT_TYPES = [
  'Day_3',
  'Week_1',
  'Pre_Return',
  'Week_1_Back',
  'Month_1',
  'Month_3',
  'Month_6',
  'Month_9',
  'Month_12',
  'Anniversary',
] as const;
export type TouchpointType = (typeof TOUCHPOINT_TYPES)[number];

// Subset of TOUCHPOINT_TYPES anchored on the return-to-work date (stored as
// `pending_touchpoint_specs` rows, materialised when RTW is confirmed). Keeping
// this as a separate union prevents writing a loss-anchored type into a row with
// `anchor = 'rtw'`. The check on the server side is the typed `RTW_PENDING_SPECS`
// constant in `apps/api/src/services/pending-touchpoints.ts`; the schema is
// `text` (no SQL CHECK) so the application-layer discipline is load-bearing.
//
// Support Framework spec §17 (Slice 3) — the spec anchors Stages 03-06
// (1/3/6/9 Month) on the RETURN-TO-WORK date, not the date of loss. So
// `Month_1/3/6/9` join the RTW set: they are pending until RTW is confirmed,
// then materialise relative to it. `Pre_Return` / `Week_1_Back` remain the
// internal sub-points of the "Return to Work" stage (the hybrid model — PO
// 2026-06-23). NB: cases created BEFORE this change keep their original
// loss-anchored `Month_*` rows (they have no month pending-specs); only new
// cases use RTW anchoring.
export const RTW_TOUCHPOINT_TYPES = [
  'Pre_Return',
  'Week_1_Back',
  'Month_1',
  'Month_3',
  'Month_6',
  'Month_9',
] as const;
export type RtwTouchpointType = (typeof RTW_TOUCHPOINT_TYPES)[number];

// Operational return-to-work touch points that fire REGARDLESS of the
// employee's grief-cadence preference (opt-out / `none` / `lighter`) — they are
// return-to-work logistics, not grief outreach (Vision & Strategy §6 carve-out;
// see `shouldDispatchTouchpoint`). This is DELIBERATELY narrower than
// `RTW_TOUCHPOINT_TYPES`: since Slice 3 the RTW set also carries the
// grief-cadence months (`Month_1/3/6/9`), which MUST respect consent and so are
// NOT listed here.
export const ALWAYS_DISPATCH_TOUCHPOINT_TYPES = ['Pre_Return', 'Week_1_Back'] as const;

// Support Framework spec §3 / §17 — the seven framework STAGES and the internal
// touch-point type(s) each groups (the hybrid model keeps multiple sub-points
// under a single stage). The display layer (Slice 11) renders by stage; this is
// the single source of that mapping.
export const SUPPORT_FRAMEWORK_STAGES = [
  { stage: 1, name: 'Early Support', anchor: 'loss', types: ['Day_3', 'Week_1'] },
  { stage: 2, name: 'Return to Work', anchor: 'rtw', types: ['Pre_Return', 'Week_1_Back'] },
  { stage: 3, name: '1 Month', anchor: 'rtw', types: ['Month_1'] },
  { stage: 4, name: '3 Months', anchor: 'rtw', types: ['Month_3'] },
  { stage: 5, name: '6 Months', anchor: 'rtw', types: ['Month_6'] },
  { stage: 6, name: '9 Months', anchor: 'rtw', types: ['Month_9'] },
  { stage: 7, name: '12 Months', anchor: 'loss', types: ['Month_12', 'Anniversary'] },
] as const satisfies ReadonlyArray<{
  stage: number;
  name: string;
  anchor: 'loss' | 'rtw';
  types: readonly TouchpointType[];
}>;

// The early, high-frequency loss-anchored touch points. This is the canonical
// classification — co-located with the type list and the RTW subset so a
// re-tuning of the series has a single place to update. Consumed by the
// `lighter` cadence (which suppresses exactly these — see
// `shouldDispatchTouchpoint` in `./preferences.ts`). If a new early touch point
// is added to `TOUCHPOINT_TYPES` / the `SERIES_DAYS_FROM_LOSS` series in
// `apps/api/src/services/touchpoints.ts`, decide here whether it is "early".
export const EARLY_TOUCHPOINT_TYPES = ['Day_3', 'Week_1'] as const;
export type EarlyTouchpointType = (typeof EARLY_TOUCHPOINT_TYPES)[number];

// Stored touch-point status (mirrors the `touchpointStatus` DB enum in
// `@besideu/db`). `Pending` is the base "active, not yet actioned" state; its
// *display* status (Scheduled / Due soon / Due today / Overdue) is DERIVED from
// `scheduledFor` at read time — see `computeDisplayStatus` in
// `./touchpoint-status.ts`, NOT stored here. `Overdue` remains in the union for
// back-compat (the repo still sets `isOverdue` as a boolean), but new code
// should prefer the display-status helper.
//
// Support Framework spec §7. `Suppressed` (employee opt-out, displays as
// "Paused") and `Skipped` mean "no manager action owed" and must be excluded
// from actionable read paths exactly like each other. `Completed` / `Skipped` /
// `HR_Review_Needed` are SET by §16 completion; `HR_Review_Needed` → `Completed`
// when HR resolves the review.
//
// §7's "In progress" and "Adjusted" are NOT stored statuses (PO 2026-06-23):
// `status` is overloaded to drive dispatch off `status = 'Pending'`, so a
// still-active touch point must stay `Pending`. "In progress" is dropped
// (binary Pending→Completed is enough); "Adjusted" is a DERIVED display badge
// (`TouchpointSummary.wasRescheduled`, from the §17 `rescheduled` audit event),
// not a status.
export const TOUCHPOINT_STATUSES = [
  'Pending',
  'Completed',
  'Overdue',
  'Done_Informally',
  'Suppressed',
  'Skipped',
  'HR_Review_Needed',
] as const;
export type TouchpointStatus = (typeof TOUCHPOINT_STATUSES)[number];

// Support Framework spec §16 — structured touch-point completion.
//
// Contact method the touch point was made through (§16 "Contact method").
export const TOUCHPOINT_CONTACT_METHODS = [
  'email',
  'phone',
  'in_person',
  'teams',
  'no_contact',
] as const;
export type TouchpointContactMethod = (typeof TOUCHPOINT_CONTACT_METHODS)[number];
export const TOUCHPOINT_CONTACT_METHOD_LABELS: Record<TouchpointContactMethod, string> = {
  email: 'Email',
  phone: 'Phone',
  in_person: 'In person',
  teams: 'Teams',
  no_contact: 'No contact requested',
};

// The §16 outcome catalog (what happened). Used both as the completion input
// and for display on the case record.
export const TOUCHPOINT_OUTCOMES = [
  'contact_made',
  'message_sent',
  'employee_requested_space',
  'practical_support_agreed',
  'hr_support_needed',
  'touch_point_moved',
  'touch_point_skipped',
  'no_action_needed',
] as const;
export type TouchpointOutcome = (typeof TOUCHPOINT_OUTCOMES)[number];
export const TOUCHPOINT_OUTCOME_LABELS: Record<TouchpointOutcome, string> = {
  contact_made: 'Contact made',
  message_sent: 'Message sent',
  employee_requested_space: 'Employee requested space',
  practical_support_agreed: 'Practical support agreed',
  hr_support_needed: 'HR support needed',
  touch_point_moved: 'Touch point moved',
  touch_point_skipped: 'Touch point skipped',
  no_action_needed: 'No action needed',
};

// Outcomes selectable in the COMPLETION flow. `touch_point_moved` is excluded:
// rescheduling is its own action (the reschedule flow, Slice 7, sets a new date
// + `Adjusted` status) rather than a terminal completion. Every value here is a
// terminal resolution that stamps `completedAt`.
export const TOUCHPOINT_COMPLETION_OUTCOMES = TOUCHPOINT_OUTCOMES.filter(
  (o): o is Exclude<TouchpointOutcome, 'touch_point_moved'> => o !== 'touch_point_moved',
);
export type TouchpointCompletionOutcome = (typeof TOUCHPOINT_COMPLETION_OUTCOMES)[number];

// §16 completion request. `outcome` is the only required field (the spec's
// "Touch point completed: Yes" plus the structured outcome). Everything else is
// optional and factual — the spec is explicit: "do not force long notes, do not
// require the manager to assess emotional state".
export const CompleteTouchpointRequestSchema = z
  .object({
    outcome: z.enum(TOUCHPOINT_COMPLETION_OUTCOMES),
    contactMethod: z.enum(TOUCHPOINT_CONTACT_METHODS).nullish(),
    followUpNeeded: z.boolean().nullish(),
    hrReviewNeeded: z.boolean().nullish(),
    notes: z.string().max(2000).nullish(),
  })
  .strict();
export type CompleteTouchpointRequest = z.infer<typeof CompleteTouchpointRequestSchema>;

// Derive the terminal stored status from a completion (§7 / §16). A skip is
// terminal-but-not-done; an HR-support outcome (or an explicit HR-review tick)
// flags the touch point for HR; everything else is a normal completion.
export function statusForCompletionOutcome(
  outcome: TouchpointCompletionOutcome,
  hrReviewNeeded?: boolean | null,
): Extract<TouchpointStatus, 'Completed' | 'Skipped' | 'HR_Review_Needed'> {
  if (outcome === 'touch_point_skipped') return 'Skipped';
  if (outcome === 'hr_support_needed' || hrReviewNeeded === true) return 'HR_Review_Needed';
  return 'Completed';
}

// Support Framework spec §23 — touch-point audit trail. Every lifecycle event
// is an append-only row (`touchpoint_audit_events`). Emission status:
//   WIRED:
//   - `created` — touch point generated (loss-anchored + RTW materialise).
//   - `due_date_calculated` — RTW dates computed at materialise.
//   - `notification_sent` — §21 cadence dispatch (offset in metadata).
//   - `guidance_opened` — HR §15 touch-point guidance opened.
//   - `completed` / `skipped` / `hr_review_requested` — §16 completion.
//   - `paused` — employee opt-out / per-touch-point `not_now`.
//   - `rescheduled` — §17 RTW date-change in-place recalculation.
//   - `hr_review_completed` — HR resolved a touch point's `HR_Review_Needed`
//     review (the per-touch-point "mark review complete" action).
// (`message_drafted` from §23 was dropped — PO 2026-06-23: there is no
// per-touch-point message-draft action; "Message sent" is captured as a §16
// completion outcome and Stage-1 first-response drafting is case-level.)
export const TOUCHPOINT_AUDIT_EVENTS = [
  'created',
  'due_date_calculated',
  'notification_sent',
  'guidance_opened',
  'completed',
  'paused',
  'skipped',
  'rescheduled',
  'hr_review_requested',
  'hr_review_completed',
] as const;
export type TouchpointAuditEvent = (typeof TOUCHPOINT_AUDIT_EVENTS)[number];

export interface TouchpointAuditEntry {
  id: string;
  touchpointId: string;
  event: TouchpointAuditEvent;
  actorUserId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string; // ISO 8601
}

// Support Framework spec §21 — notification cadence. The DUE-date reminder
// (manager) and the first overdue nudge (manager, ~2 days) are already covered
// by `touchpoint_reminder` + `manager_touchpoint_overdue`. These four offsets
// are the gaps Slice 6 adds:
//   - `7d_before` / `2d_before` — manager "prepare" reminders before the date.
//   - `5d_overdue_hr` / `10d_overdue_hr` — HR oversight, then escalation.
// Each fires at most once per touch point (deduped via a `notification_sent`
// audit event carrying the offset — see `runTouchpointCadenceSweep`).
export const TOUCHPOINT_NOTIFICATION_OFFSETS = [
  '7d_before',
  '2d_before',
  '5d_overdue_hr',
  '10d_overdue_hr',
] as const;
export type TouchpointNotificationOffset = (typeof TOUCHPOINT_NOTIFICATION_OFFSETS)[number];

// The `5d_overdue_hr` / `10d_overdue_hr` tiers go to HR (oversight/escalation);
// the `*_before` tiers go to the manager.
export function isHrNotificationOffset(offset: TouchpointNotificationOffset): boolean {
  return offset === '5d_overdue_hr' || offset === '10d_overdue_hr';
}

// Pure: given a touch point's scheduled date and "now", which §21 cadence
// notification (if any) is due today. Windows are disjoint so at most one fires
// per touch point per sweep; the daily cron + the send-once audit dedup mean a
// touch point reaches each tier exactly once even though the cron skips
// weekends. Returns null for the due-date / 0-4-day-overdue band (covered by
// the existing reminder + manager nudge).
export function touchpointNotificationOffsetFor(
  scheduledFor: Date,
  now: Date,
): TouchpointNotificationOffset | null {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const deltaDays = Math.floor((now.getTime() - scheduledFor.getTime()) / MS_PER_DAY);
  if (deltaDays >= -7 && deltaDays <= -3) return '7d_before';
  if (deltaDays >= -2 && deltaDays <= -1) return '2d_before';
  if (deltaDays >= 5 && deltaDays <= 9) return '5d_overdue_hr';
  if (deltaDays >= 10) return '10d_overdue_hr';
  return null;
}

// Per-touch-point employee preferences — Employee Portal & App spec §9.4.3.
// For a single touch point the employee can indicate a preferred contact
// person / channel, a scheduling request, and a team-sharing choice. All
// optional (null = not chosen). `request: 'not_now'` is the one with a
// behavioural effect: the touch point is paused (not dispatched, not flagged
// overdue, no manager nudge) but stays visible to the employee.
export const TOUCHPOINT_PREF_CONTACT_PERSONS = ['manager', 'hr'] as const;
export type TouchpointPrefContactPerson = (typeof TOUCHPOINT_PREF_CONTACT_PERSONS)[number];
export const TOUCHPOINT_PREF_REQUESTS = ['not_now', 'move_later', 'sooner'] as const;
export type TouchpointPrefRequest = (typeof TOUCHPOINT_PREF_REQUESTS)[number];

export interface TouchpointPreference {
  // "happy for my manager to contact me" / "would prefer HR to contact me".
  contactPerson: TouchpointPrefContactPerson | null;
  // "would prefer email".
  preferEmail: boolean | null;
  // "not to be contacted right now" (pauses) / "move this touch point" /
  // "speak to someone sooner".
  request: TouchpointPrefRequest | null;
  // "do not want this shared with my wider team".
  doNotShareWithTeam: boolean | null;
}

export const SetTouchpointPreferenceRequestSchema = z
  .object({
    contactPerson: z.enum(TOUCHPOINT_PREF_CONTACT_PERSONS).nullable(),
    preferEmail: z.boolean().nullable(),
    request: z.enum(TOUCHPOINT_PREF_REQUESTS).nullable(),
    doNotShareWithTeam: z.boolean().nullable(),
  })
  .strict();
export type SetTouchpointPreferenceRequest = z.infer<typeof SetTouchpointPreferenceRequestSchema>;

export interface TouchpointSummary {
  id: string;
  type: TouchpointType;
  status: TouchpointStatus;
  scheduledFor: string; // ISO YYYY-MM-DD
  dispatchedAt: string | null; // ISO 8601
  completedAt: string | null; // ISO 8601
  // Derived: status === 'Pending' && scheduledFor + 48h < now (DESIGN §9.2 #5).
  isOverdue: boolean;
  // Support Framework spec §7 "Adjusted" — derived, true when this touch point
  // has a `rescheduled` audit event (its date moved on an RTW date change, §17).
  // Surfaced as an "Updated" badge; NOT a stored status (see TOUCHPOINT_STATUSES).
  wasRescheduled: boolean;
  // Support Framework spec §16 — structured completion, surfaced on the case
  // record (§9 HR case view / Manager view). All null until the touch point is
  // completed. `notes` mirrors the stored `manager_notes`.
  outcome: TouchpointOutcome | null;
  contactMethod: TouchpointContactMethod | null;
  followUpNeeded: boolean | null;
  hrReviewNeeded: boolean | null;
  notes: string | null;
  // §9.4.3 — the employee's per-touch-point preference (null when none set).
  // Surfaced to whoever sees the touch point so they can honour it: the
  // employee on their own portal read, and the manager/HR on the case view for
  // touch points that are still going ahead (paused `not_now` touch points are
  // excluded from the manager/HR read). The Manager view has the team-sharing
  // field (`doNotShareWithTeam`) withheld per the §11.2 matrix; HR sees it.
  preference: TouchpointPreference | null;
}

// Support Framework spec §3/§17 — a not-yet-materialised RTW-anchored touch
// point (`pending_touchpoint_specs` row, `pending = true`). Surfaced on the
// case-detail timeline so all 7 stages render even before the return-to-work
// date is confirmed (Stages 2-6 would otherwise have zero rows and vanish —
// see `CaseDetailView`'s stage loop). Distinct from `TouchpointSummary`
// rather than a variant of it: a pending spec has no status, completion, or
// preference — nothing has been scheduled yet.
export interface PendingTouchpointSummary {
  id: string;
  type: TouchpointType;
  // Reserved for a future "estimated date" feature — `scheduled_for_estimate`
  // is only ever written by `materializePendingRtwSeries` in the same write
  // that promotes the spec (`pending` flips to false), so a row this API
  // surfaces (still `pending = true`) is unconditionally null today. Typed
  // nullable rather than dropped so a future write path that estimates ahead
  // of promotion doesn't need a wire-shape change.
  scheduledForEstimate: string | null; // ISO YYYY-MM-DD
}

// Detail payload — shared between HR and Manager case-detail endpoints.
export interface CaseDetail {
  case: CaseSummary;
  touchpoints: TouchpointSummary[];
  pendingTouchpoints: PendingTouchpointSummary[];
}

// Narrowed view of a case for the employee portal. Drops fields that have no
// reason to cross the API boundary to the bereaved employee:
//   - `employee.id` / `employee.name` — the employee knows themselves
//   - `manager.id` — UUID exposure with no UI use
//   - `firstResponseSentAt` — manager-side workflow metadata
// Per DESIGN §6.2: "must strip out any manager notes or HR metadata."
export interface EmployeeCaseSummary {
  id: string;
  managerName: string;
  deceasedRelationship: DeceasedRelationship;
  dateOfLoss: string;
  status: CaseStatus;
  returnToWorkDate: string | null;
  createdAt: string;
}

// Slice 17 — RAG-driven leave entitlements payload (DESIGN §6.2 Action 1).
// The employee portal calls `computeLeaveEntitlements` server-side on every
// portal load; the result is a structured Gemini output grounded on the
// org's policy chunks (Slice 16's `PolicyRag` DO). Citations point back at
// the source policy filenames so the employee can verify.
//
// Failure-open posture: if the org has no indexed policies, or retrieval
// returned 0 hits, or Vertex flaked, the portal returns `entitlements: null`
// and the UI renders the same placeholder copy as pre-Slice-17. Distinct
// from HR Maeve / Manager guidance where retrieval failures DO 502 — for
// the employee portal a Vertex blip must not break portal load.
export interface EmployeeEntitlements {
  // Free-text summary the model produces from the retrieved excerpts. Can be
  // multi-paragraph. NEVER inferred without policy grounding — the model is
  // instructed to say "your policy doesn't specify" rather than guess.
  summary: string;
  // Optional structured highlights. Both nullable because the org's policy
  // may not specify either (e.g. "follow statutory" with no day count, or
  // unpaid leave at manager's discretion). Null = "policy doesn't say",
  // distinct from an absent entitlement key.
  leaveDays: string | null;
  payStatus: string | null;
  // The filenames of policies whose chunks contributed to this answer.
  // Lets the UI render a "based on: Handbook.pdf, Leave Policy.docx" line.
  // Deduplicated and order-preserved by first appearance in the chunk list.
  citations: string[];
}

// Employee portal — DESIGN §6.2. `case` is null when the employee has been
// invited but no bereavement has been logged for them yet. `entitlements`
// is null when no RAG-grounded entitlements can be produced (no indexed
// policies, retrieval miss, or Vertex flake — see `EmployeeEntitlements`).
// `rtwPlan` is null until the Manager submits one (Slice 18 / DESIGN §6.5).
// The Employee-portal payload uses the narrowed `EmployeeRtwPlan` shape
// which structurally omits `managerPrivateNotes` — the type itself is the
// privacy guarantee, with the server-side strip as defence in depth.
export interface EmployeePortal {
  case: EmployeeCaseSummary | null;
  touchpoints: TouchpointSummary[];
  entitlements: EmployeeEntitlements | null;
  rtwPlan: EmployeeRtwPlan | null;
  // Employee-set support preferences (Vision & Strategy §5/§6). Always
  // present — defaults when the employee has never set any.
  preferences: EmployeeSupportPreferences;
}

// Slice 8 — HR Admin new-case form data (DESIGN §6.1).
//
// PO Spec §2 (2026-06-21) — the case-create form is 3 fields only
// (Employee, Relationship, Date of loss), so the loader no longer returns
// the `managers` list. Each employee row carries `mappedManagerId` so the
// UI can show "Manager: <name>" as a read-only confirmation when one
// exists, and an inline "set mapping in Members" deep link when it
// doesn't (PO Spec §1.3: an HR Admin can only create a case when the
// employee has a manager mapping).
export interface HrCaseCreateFormData {
  employees: Array<{ id: string; name: string; mappedManagerId: string | null }>;
}

// Slice 18 — Return-to-Work plan API contracts. DESIGN §3.2.2 / §6.5 / §9.4 #3.
//
// Two visibility tiers:
//   - `ReturnToWorkPlan`     — Manager + HR view, full row including
//                              `managerPrivateNotes`.
//   - `EmployeeRtwPlan`      — Employee portal view, `managerPrivateNotes`
//                              stripped server-side per DESIGN §3.2.2.
// The shapes are deliberately distinct types (not one type with optional
// field) so a downstream component can't accidentally read the private notes
// from an Employee-context payload.

export const PHASED_RETURN_VALUES = ['Full_Time', 'Part_Time', 'Staggered'] as const;
export type PhasedReturn = (typeof PHASED_RETURN_VALUES)[number];

interface RtwPlanCore {
  id: string;
  caseId: string;
  workloadExpectations: string;
  phasedReturn: PhasedReturn;
  // Date-only because times-of-day aren't meaningful for the planned ramp.
  // Null for `Full_Time` returns (no ramp) and acceptable as "TBD" for
  // `Part_Time` / `Staggered` plans where the Manager hasn't committed.
  targetFullLoadDate: string | null; // ISO YYYY-MM-DD
  supportPreferences: string;
  submittedByUserId: string;
  submittedAt: string; // ISO 8601
  // Null until the Employee acknowledges; gates the pre-return automation.
  acknowledgedAt: string | null; // ISO 8601
}

// Manager + HR-visible row. The HR Safety Contact and the assigned Manager
// both see this shape. The full case-detail endpoints (`/api/cases/:id` for
// Manager, `/api/hr/cases/:id` for HR) embed it as `rtwPlan` on the response.
export interface ReturnToWorkPlan extends RtwPlanCore {
  managerPrivateNotes: string | null;
}

// Employee portal view. Same core fields, NO `managerPrivateNotes` — the
// type-level distinction is the structural guarantee, with a server-side
// strip in the `/api/employee/rtw-plan/:caseId` handler as defence in depth.
export type EmployeeRtwPlan = RtwPlanCore;

// Manager submit / edit request body. Used for both POST (initial submit)
// and PATCH (pre-ack edit). Server-side gate: PATCH refuses on a plan
// that's already acknowledged (`acknowledgedAt != null`) with a 409.
//
// `workloadExpectations` and `supportPreferences` are free text capped at
// 5000 chars so a runaway paste can't blow the row. `targetFullLoadDate`
// is optional/nullable because Full_Time returns don't have a ramp.
// `managerPrivateNotes` is also optional + capped.
export const SubmitRtwPlanRequestSchema = z.object({
  workloadExpectations: z.string().min(1).max(5_000),
  phasedReturn: z.enum(PHASED_RETURN_VALUES),
  targetFullLoadDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .nullable()
    .optional(),
  supportPreferences: z.string().min(1).max(5_000),
  managerPrivateNotes: z.string().max(5_000).nullable().optional(),
});
export type SubmitRtwPlanRequest = z.infer<typeof SubmitRtwPlanRequestSchema>;

// Manager case-detail extension — embeds the (optional) RTW plan into the
// existing `CaseDetail`. The Manager UI uses this to render the form-or-
// summary state. `null` means no plan yet (either never submitted, or the
// `return_to_work_date` changed and the plan was hard-deleted per the
// invalidation rule).
export interface CaseDetailWithRtw {
  case: CaseSummary;
  touchpoints: TouchpointSummary[];
  pendingTouchpoints: PendingTouchpointSummary[];
  rtwPlan: ReturnToWorkPlan | null;
  // The employee's §11/§16/§17 preferences, filtered through the §11.2
  // visibility matrix for the requesting role (HR sees more than Manager). The
  // server builds this with `employeePreferencesForRole`, so a field the matrix
  // denies the role never reaches the wire. See `@besideu/shared/preference-visibility`.
  employeePreferences: RoleVisiblePreferences;
  // Employee-recorded significant dates, role-projected via
  // `significantDatesForRole` — HR gets the full list (`{visible}`), Manager
  // gets only the coarse signal (`{hasUpcoming}`). See
  // `@besideu/shared/significant-dates`.
  significantDates: SignificantDatesForRole;
}

// `POST /api/cases/:id/return-to-work-date` body — Manager-only setter for
// `bereavement_cases.return_to_work_date`. Nullable so the Manager can
// retract a date (also invalidates any existing RTW plan per the
// "Plan invalidated; Manager must re-submit" rule). Action-shaped
// (not PATCH) to match the existing `mark-first-response-sent` pattern.
// Support Framework spec §17 — when the return-to-work date CHANGES, the
// system asks how to handle the already-scheduled RTW-anchored support dates
// (the 1/3/6/9-month touch points):
//   - `recalculate` — recompute them relative to the new date (default).
//   - `keep`        — leave them at their current dates.
//   - `ask_hr`      — leave them and flag the case for HR review.
// Ignored on first-set (there is nothing to recalculate). The plan-tied
// Pre_Return / Week_1_Back touch points always follow RTW-plan invalidation
// regardless of this choice (they are plan artifacts, re-derived on plan
// re-submission).
export const RTW_RECALCULATE_CHOICES = ['recalculate', 'keep', 'ask_hr'] as const;
export type RtwRecalculateChoice = (typeof RTW_RECALCULATE_CHOICES)[number];

export const SetReturnToWorkDateRequestSchema = z.object({
  returnToWorkDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .nullable(),
  recalculate: z.enum(RTW_RECALCULATE_CHOICES).optional(),
});
export type SetReturnToWorkDateRequest = z.infer<typeof SetReturnToWorkDateRequestSchema>;

// PO Spec Phase 4 §3 (2026-06-21) — "Resources Accessed" event. The
// Employee's portal/companion fires this on a crisis-link click or a
// policy-chunk read. The schema is narrow on purpose: only the resource
// family, never the URL or any user-facing copy (the family is enough
// for HR to know "this Employee engaged with a crisis resource" without
// knowing which specific link).
//
// `resourceKind` mirrors the `resource_access_events.resource_kind`
// enum in `@besideu/db` (Phase 4.4). Adding a kind is type-only there.
// `resourceRef` is an optional opaque handle (e.g. the chunk id hash for
// `policy_chunk_read`, the resource family for `crisis_link_click`).
// Server-side validation: the ref must be ≤ 256 chars to bound the row
// size — a runaway client posting a multi-KB string would still work
// but would inflate every row in the append-only table.
export const RESOURCE_ACCESS_KINDS = ['crisis_link_click', 'policy_chunk_read'] as const;
export type ResourceAccessKind = (typeof RESOURCE_ACCESS_KINDS)[number];
export const RecordResourceAccessRequestSchema = z.object({
  resourceKind: z.enum(RESOURCE_ACCESS_KINDS),
  resourceRef: z.string().max(256).nullable().optional(),
});
export type RecordResourceAccessRequest = z.infer<typeof RecordResourceAccessRequestSchema>;
